import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check } from 'lucide-react';
import { cn } from 'lib/utils';

interface ChoiceCardProps {
  label: string;
  sub?: string;
  icon?: LucideIcon;
  selected: boolean;
  /** A tick in a square for "pick any", a dot in a circle for "pick one". */
  multiple?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

/**
 * One answer of an onboarding question. It only marks the answer: moving on is always the learner's
 * own "Davom etish", so a double tap or a second thought never skips a question.
 */
export function ChoiceCard({ label, sub, icon: Icon, selected, multiple, disabled, onSelect }: ChoiceCardProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex min-h-[56px] w-full items-center justify-between gap-3 rounded-md border p-4 text-left transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        selected
          ? 'border-[#10b981] bg-emerald-50/60 ring-2 ring-[#10b981] dark:bg-emerald-950/30'
          : 'border-neutral-200 bg-white hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-700',
      )}
    >
      <span className="flex min-w-0 items-center gap-3.5">
        {Icon && (
          <span
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-md',
              selected ? 'bg-[#10b981] text-white' : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
            )}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </span>
        )}
        <span className="min-w-0">
          <span className="block text-sm font-bold text-neutral-900 dark:text-white">{label}</span>
          {sub && <span className="block text-xs text-neutral-500">{sub}</span>}
        </span>
      </span>
      <span
        aria-hidden
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center border',
          multiple ? 'rounded-[5px]' : 'rounded-full',
          selected ? 'border-[#10b981] bg-[#10b981] text-white' : 'border-neutral-300 dark:border-neutral-700',
        )}
      >
        {selected && <Check className="h-3 w-3" />}
      </span>
    </button>
  );
}

export default ChoiceCard;
